import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calculator, DollarSign, Moon, Sun, Menu, X, HelpCircle, Car, TrendingUp,
  ExternalLink, Percent, Heart, BookOpen, ChevronDown, Zap, BarChart3, Tag, Mail, Map, Leaf
} from "lucide-react";
import { useState, useEffect } from "react";
import { NewsletterModal } from "./NewsletterModal";
import axios from "axios";
import { API } from "@/config";

export const Navbar = ({ darkMode, setDarkMode }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [linkInfo, setLinkInfo] = useState("");
  const [linkDonar, setLinkDonar] = useState("");
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        setLinkInfo(response?.data?.link_info || "/docs/");
        setLinkDonar(response?.data?.link_donar || "");
      } catch (error) {
        setLinkInfo("/docs/");
      }
    };
    fetchConfig();
  }, []);

  const isActive = (path) => location.pathname === path;
  const isInGroup = (paths) => paths.some(p => location.pathname === p);

  // Mobile sub-items expand state
  const [mobileCalcOpen, setMobileCalcOpen] = useState(false);
  const [mobileVehOpen, setMobileVehOpen] = useState(false);
  const [mobileTarifasOpen, setMobileTarifasOpen] = useState(false);

  return (<>
    <nav className={`${location.pathname === "/" ? "absolute" : "sticky"} top-0 w-full z-50 bg-primary text-primary-foreground shadow-lg backdrop-blur-md`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="bg-white/10 p-1.5 rounded-xl">
              <img
                src="/logo.svg"
                alt="Movilidad Eléctrica"
                className="h-8 w-8"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) { e.target.nextSibling.style.display = 'block'; }
                }}
              />
              <svg
                className="h-8 w-8 text-accent hidden"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <div className="hidden sm:block">
              <span role="heading" aria-level="1" className="text-lg font-bold font-heading tracking-tight block">
                Movilidad Eléctrica
              </span>
              <p className="text-xs text-white/70">por un futuro sostenible</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Calculadoras Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isInGroup(["/comparador", "/inversion", "/tir", "/calculadora/ruta", "/tco-avanzado", "/salud-bateria", "/creditos-carbono"]) ? "secondary" : "ghost"}
                  className={`gap-1.5 ${!isInGroup(["/comparador", "/inversion", "/tir", "/calculadora/ruta", "/tco-avanzado", "/salud-bateria", "/creditos-carbono"]) && "text-white hover:text-white hover:bg-white/10"}`}
                  data-testid="nav-calculadoras"
                >
                  <Calculator className="h-4 w-4" />
                  Calculadoras
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/comparador" className="flex items-center gap-2 cursor-pointer">
                    <Calculator className="h-4 w-4" />
                    Comparador
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/inversion" className="flex items-center gap-2 cursor-pointer">
                    <TrendingUp className="h-4 w-4" />
                    Inversión
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tir" className="flex items-center gap-2 cursor-pointer">
                    <Percent className="h-4 w-4" />
                    TIR
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculadora/ruta" className="flex items-center gap-2 cursor-pointer">
                    <Map className="h-4 w-4" />
                    Ruta
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tco-avanzado" className="flex items-center gap-2 cursor-pointer text-amber-600 dark:text-amber-500 font-medium bg-amber-50 dark:bg-amber-950/30">
                    <TrendingUp className="h-4 w-4" />
                    Flotas PRO
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/salud-bateria" className="flex items-center gap-2 cursor-pointer text-emerald-600 dark:text-emerald-500 font-medium">
                    <Zap className="h-4 w-4" />
                    Salud Batería
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/creditos-carbono" className="flex items-center gap-2 cursor-pointer text-green-600 dark:text-green-400 font-medium">
                    <Leaf className="h-4 w-4" />
                    Créditos CO₂
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Vehículos Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isInGroup(["/vehiculos", "/top-eficiencia", "/precios", "/estadisticas", "/estadisticas-precios", "/estadisticasparque"]) ? "secondary" : "ghost"}
                  className={`gap-1.5 ${!isInGroup(["/vehiculos", "/top-eficiencia", "/precios", "/estadisticas", "/estadisticas-precios", "/estadisticasparque"]) && "text-white hover:text-white hover:bg-white/10"}`}
                  data-testid="nav-vehiculos"
                >
                  <Car className="h-4 w-4" />
                  Vehículos
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/vehiculos" className="flex items-center gap-2 cursor-pointer">
                    <Car className="h-4 w-4" />
                    Vehículos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/top-eficiencia" className="flex items-center gap-2 cursor-pointer">
                    <BarChart3 className="h-4 w-4" />
                    TOP Eficiencia
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/precios" className="flex items-center gap-2 cursor-pointer">
                    <DollarSign className="h-4 w-4" />
                    Precios
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/estadisticas" className="flex items-center gap-2 cursor-pointer">
                    <TrendingUp className="h-4 w-4" />
                    Estadísticas
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/estadisticas-precios" className="flex items-center gap-2 cursor-pointer">
                    <Tag className="h-4 w-4" />
                    Estadísticas de Precios
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/estadisticasparque" className="flex items-center gap-2 cursor-pointer">
                    <BarChart3 className="h-4 w-4" />
                    Parque Automotor
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>



            {/* Blog */}
            <Link to="/blog">
              <Button
                variant={isActive("/blog") || location.pathname.startsWith("/blog/") ? "secondary" : "ghost"}
                className={`gap-2 ${!(isActive("/blog") || location.pathname.startsWith("/blog/")) && "text-white hover:text-white hover:bg-white/10"}`}
                data-testid="nav-blog"
              >
                <BookOpen className="h-4 w-4" />
                Blog
              </Button>
            </Link>

            {/* Tarifas Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isInGroup(["/tarifas", "/tarifas/historicas"]) ? "secondary" : "ghost"}
                  className={`gap-1.5 ${!isInGroup(["/tarifas", "/tarifas/historicas"]) && "text-white hover:text-white hover:bg-white/10"}`}
                  data-testid="nav-tarifas"
                >
                  <DollarSign className="h-4 w-4" />
                  Tarifas
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/tarifas" className="flex items-center gap-2 cursor-pointer">
                    <DollarSign className="h-4 w-4" />
                    Actuales
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tarifas/historicas" className="flex items-center gap-2 cursor-pointer">
                    <TrendingUp className="h-4 w-4" />
                    Históricas
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* FAQ */}
            <Link to="/faq">
              <Button
                variant={isActive("/faq") ? "secondary" : "ghost"}
                className={`gap-2 ${!isActive("/faq") && "text-white hover:text-white hover:bg-white/10"}`}
                data-testid="nav-faq"
              >
                <HelpCircle className="h-4 w-4" />
                FAQ
              </Button>
            </Link>

            {/* Docs */}
            {linkInfo && (
              <a
                href={linkInfo}
                target={linkInfo.startsWith('/') ? "_self" : "_blank"}
                rel={linkInfo.startsWith('/') ? undefined : "noopener noreferrer"}
              >
                <Button
                  variant="ghost"
                  className="gap-2 text-white hover:text-white hover:bg-white/10"
                  data-testid="nav-docs"
                >
                  <ExternalLink className="h-4 w-4" />
                  Docs
                </Button>
              </a>
            )}

            {/* Donar Corazón */}
            {linkDonar && (
              <a href={linkDonar} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="nav-donar"
                  title="Donar"
                >
                  <Heart className="h-5 w-5 fill-current" />
                </Button>
              </a>
            )}

            {/* Newsletter */}
            <Button
              variant="ghost"
              size="icon"
              className="text-blue-300 hover:text-blue-200 hover:bg-blue-500/10"
              data-testid="nav-newsletter"
              title="Suscribirse al newsletter"
              onClick={() => setNewsletterOpen(true)}
            >
              <Mail className="h-5 w-5" />
            </Button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <div className="flex items-center gap-2" data-testid="theme-toggle">
              <Sun className="h-4 w-4 text-accent" />
              <Switch
                checked={darkMode}
                onCheckedChange={setDarkMode}
                className="data-[state=checked]:bg-accent"
                aria-label="Cambiar tema (oscuro/claro)"
              />
              <Moon className="h-4 w-4" />
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 animate-fade-in">
            <div className="flex flex-col gap-1">
              {/* Calculadoras group */}
              <button
                onClick={() => setMobileCalcOpen(!mobileCalcOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white hover:bg-white/10 font-medium"
              >
                <span className="flex items-center gap-2"><Calculator className="h-4 w-4" /> Calculadoras</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileCalcOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileCalcOpen && (
                <div className="pl-8 flex flex-col gap-1">
                  <MobileLink to="/comparador" label="Comparador" icon={Calculator} active={isActive("/comparador")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/inversion" label="Inversión" icon={TrendingUp} active={isActive("/inversion")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/tir" label="TIR" icon={Percent} active={isActive("/tir")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/calculadora/ruta" label="Ruta" icon={Map} active={isActive("/calculadora/ruta")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/tco-avanzado" label="Flotas PRO" icon={TrendingUp} active={isActive("/tco-avanzado")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/salud-bateria" label="Salud Batería" icon={Zap} active={isActive("/salud-bateria")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/creditos-carbono" label="Créditos CO₂" icon={Leaf} active={isActive("/creditos-carbono")} onClick={() => setMobileMenuOpen(false)} />
                </div>
              )}

              {/* Vehículos group */}
              <button
                onClick={() => setMobileVehOpen(!mobileVehOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white hover:bg-white/10 font-medium"
              >
                <span className="flex items-center gap-2"><Car className="h-4 w-4" /> Vehículos</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileVehOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileVehOpen && (
                <div className="pl-8 flex flex-col gap-1">
                  <MobileLink to="/vehiculos" label="Vehículos" icon={Car} active={isActive("/vehiculos")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/top-eficiencia" label="TOP Eficiencia" icon={BarChart3} active={isActive("/top-eficiencia")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/precios" label="Precios" icon={DollarSign} active={isActive("/precios")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/estadisticas" label="Estadísticas" icon={TrendingUp} active={isActive("/estadisticas")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/estadisticas-precios" label="Estadísticas Precios" icon={Tag} active={isActive("/estadisticas-precios")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/estadisticasparque" label="Parque Automotor" icon={BarChart3} active={isActive("/estadisticasparque")} onClick={() => setMobileMenuOpen(false)} />
                </div>
              )}

              {/* Links directos */}
              <MobileLink to="/blog" label="Blog" icon={BookOpen} active={isActive("/blog") || location.pathname.startsWith("/blog/")} onClick={() => setMobileMenuOpen(false)} />
              {/* Tarifas group */}
              <button
                onClick={() => setMobileTarifasOpen(!mobileTarifasOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white hover:bg-white/10 font-medium"
              >
                <span className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Tarifas</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileTarifasOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileTarifasOpen && (
                <div className="pl-8 flex flex-col gap-1">
                  <MobileLink to="/tarifas" label="Actuales" icon={DollarSign} active={isActive("/tarifas")} onClick={() => setMobileMenuOpen(false)} />
                  <MobileLink to="/tarifas/historicas" label="Históricas" icon={TrendingUp} active={isActive("/tarifas/historicas")} onClick={() => setMobileMenuOpen(false)} />
                </div>
              )}
              <MobileLink to="/faq" label="FAQ" icon={HelpCircle} active={isActive("/faq")} onClick={() => setMobileMenuOpen(false)} />

              {/* Docs */}
              {linkInfo && (
                <a
                  href={linkInfo}
                  target={linkInfo.startsWith('/') ? "_self" : "_blank"}
                  rel={linkInfo.startsWith('/') ? undefined : "noopener noreferrer"}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-white hover:bg-white/10"
                >
                  <ExternalLink className="h-4 w-4" />
                  Docs
                </a>
              )}

              {/* Donar */}
              {linkDonar && (
                <a
                  href={linkDonar}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 font-medium"
                >
                  <Heart className="h-4 w-4 fill-current" />
                  Donar
                </a>
              )}

              {/* Newsletter */}
              <button
                onClick={() => { setNewsletterOpen(true); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-blue-300 hover:bg-blue-500/10 font-medium w-full text-left"
                data-testid="mobile-nav-newsletter"
              >
                <Mail className="h-4 w-4" />
                Newsletter
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>

    {/* Newsletter Modal — montado fuera del nav para evitar overflow:hidden */}
    <NewsletterModal
      isOpen={newsletterOpen}
      onClose={() => setNewsletterOpen(false)}
      listaTipo="general"
    />
  </>);
};

const MobileLink = ({ to, label, icon: Icon, active, onClick }) => (
  <Link to={to} onClick={onClick}>
    <Button
      variant={active ? "secondary" : "ghost"}
      className={`w-full justify-start gap-2 ${!active && "text-white hover:text-white hover:bg-white/10"}`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Button>
  </Link>
);
